import {
  NetworkData,
  Node,
  Edge,
  NodeType,
  NodeTypeFilterSettings,
  DEFAULT_NODE_TYPE_FILTERS,
  NodeIDs,
} from '../types/cytoscape';

// 基本フィルタリング関数 - 他のフィルタリング関数で再利用可能
const filterValidConnections = (nodes: Node[], edges: Edge[]): Edge[] => {
  const validNodeIds = new Set(nodes.map((node) => node.id));
  return edges.filter(
    (edge) => validNodeIds.has(edge.source) && validNodeIds.has(edge.target)
  );
};

// ソーシャルイシューによるフィルタリング機能を追加
export const filterBySocialIssues = (
  data: NetworkData,
  showOnlySocialIssues: boolean
): NetworkData => {
  if (!data?.nodes || !data?.edges || !showOnlySocialIssues) {
    return data; // フィルターがオフか、データが不正なら元のデータを返す
  }

  // ソーシャルイシューのノードのみをフィルタリング
  const filteredNodes = data.nodes.filter(
    (node) =>
      node.nodeType === NodeType.CROSS_ISSUE ||
      node.nodeType === NodeType.SOCIAL_ISSUE ||
      node.nodeType === NodeType.THEME
  );

  // 有効なエッジのみを保持
  const validEdges = filterValidConnections(filteredNodes, data.edges);

  return {
    nodes: filteredNodes,
    edges: validEdges,
  };
};

// 文書ゼロのタグをフィルタリング（新機能）
export const filterTagsWithoutDocuments = (
  data: NetworkData,
  hideTagsWithoutDocuments: boolean
): NetworkData => {
  if (!data?.nodes || !data?.edges || !hideTagsWithoutDocuments) {
    return data; // フィルターがオフか、データが不正なら元のデータを返す
  }

  // ドキュメント数が1より大きいノードのみ
  const filteredNodes = data.nodes.filter((node) => node.weight > 1);

  // 有効なエッジのみを保持
  const validEdges = filterValidConnections(filteredNodes, data.edges);

  return {
    nodes: filteredNodes,
    edges: validEdges,
  };
};

// 重みと頻度ベースのフィルタリング
export const filterByWeightAndFrequency = (
  data: NetworkData,
  minEdgeWeight: number,
  minNodeWeight: number,
  minNodeFrequency: number
): NetworkData => {
  if (!data?.nodes || !data?.edges) {
    return { nodes: [], edges: [] };
  }

  if (data.nodes.length === 0) {
    console.warn('ノードがありません。フィルタリングをスキップします。');
    return { nodes: [], edges: [] };
  }
  if (data.edges.length === 0) {
    console.warn('エッジがありません。フィルタリングをスキップします。');
    return { nodes: [], edges: [] };
  }

  // ノードのフィルタリング
  const filteredNodes = data.nodes.filter(
    (node) => node.weight >= minNodeWeight && node.frequency >= minNodeFrequency
  );

  // エッジのフィルタリング
  const filteredEdges = data.edges.filter(
    (edge) => edge.weight >= minEdgeWeight
  );

  // 有効な接続のみを保持
  const validEdges = filterValidConnections(filteredNodes, filteredEdges);

  // エッジが0の場合の緩和処理を追加
  if (validEdges.length === 0 && filteredNodes.length > 1) {
    // 代替案1: 元のエッジをすべて使用（フィルタリング条件を無視）
    const fallbackEdges = filterValidConnections(filteredNodes, data.edges);

    if (fallbackEdges.length > 0) {
      return {
        nodes: filteredNodes,
        edges: fallbackEdges,
      };
    }

    // 代替案2: フィルタリングされたノード間で強制的に最低限のエッジを作成
    // これは最後の手段として、視覚的な接続を確保するため
    if (filteredNodes.length > 1) {
      const minimumEdges: Edge[] = [];
      // 少なくとも連結グラフになるように最低限のエッジを追加
      for (let i = 1; i < filteredNodes.length; i++) {
        minimumEdges.push({
          source: filteredNodes[0].id,
          target: filteredNodes[i].id,
          weight: 0.1, // 最小重み
          id: `fallback_edge_${filteredNodes[0].id}_${filteredNodes[i].id}`,
        });
      }
      return {
        nodes: filteredNodes,
        edges: minimumEdges,
      };
    }
  }

  return {
    nodes: filteredNodes,
    edges: validEdges,
  };
};

// タグベースのフィルタリング
export const filterByFilterItems = (
  data: NetworkData,
  filterConditionNodeIDs: NodeIDs | undefined
): NetworkData => {
  if (!data?.nodes || !data?.edges) {
    return { nodes: [], edges: [] };
  }

  if (!filterConditionNodeIDs) {
    return data; // フィルターがない場合は元のデータを返す
  }

  // NodeIDs を配列として扱えるよう変換
  const nodeIdsArray = Array.isArray(filterConditionNodeIDs)
    ? filterConditionNodeIDs
    : Object.values(filterConditionNodeIDs);

  if (nodeIdsArray.length === 0) {
    return data; // フィルターが空の場合は元のデータを返す
  }

  // 1. まずタグに一致するノードを特定
  const matchedNodes = data.nodes.filter((node) =>
    nodeIdsArray.some((tag) => tag === node.label)
  );

  // 2. 一致したノードのIDセットを作成（高速ルックアップ用）
  const matchedNodeIds = new Set(matchedNodes.map((node) => node.id));

  // 3. 一致したノードに関連するノードも含める（カテゴリノードなど）
  const relatedNodeIds = new Set<string>();
  data.edges.forEach((edge) => {
    if (matchedNodeIds.has(edge.source)) {
      relatedNodeIds.add(edge.target);
    }
    if (matchedNodeIds.has(edge.target)) {
      relatedNodeIds.add(edge.source);
    }
  });

  // 4. フィルタリング対象に含めるすべてのノードのIDを結合
  const allRelevantNodeIds = new Set([...matchedNodeIds, ...relatedNodeIds]);

  // 5. 関連ノードすべてを含めたフィルタリング
  const filteredNodes = data.nodes
    .filter((node) => allRelevantNodeIds.has(node.id))
    .map((node) => {
      // 一致したタグのみ重みを更新
      return node;
    });

  // 6. 両端のノードが含まれるエッジのみをフィルタリング
  const filteredEdges = data.edges.filter(
    (edge: Edge) =>
      allRelevantNodeIds.has(edge.source) &&
      allRelevantNodeIds.has(edge.target) &&
      edge.virtual != true // virtualなエッジは除外
  );
  // 7. 有効な接続のみを保持
  const validEdges = filterValidConnections(filteredNodes, filteredEdges);

  return {
    nodes: filteredNodes,
    edges: validEdges,
  };
};

// ノードタイプによるフィルタリング機能
export const filterByNodeType = (
  data: NetworkData,
  nodeTypeFilters: NodeTypeFilterSettings
): NetworkData => {
  if (!data?.nodes || !data?.edges) {
    return { nodes: [], edges: [] };
  }

  if (data.nodes.length === 0) {
    return { nodes: [], edges: [] };
  }
  if (data.edges.length === 0) {
    return { nodes: [], edges: [] };
  }

  // すべてのノードタイプが非表示の場合は空のデータを返す
  const allDisabled = Object.values(nodeTypeFilters).every(
    (visible) => !visible
  );
  if (allDisabled) {
    return { nodes: [], edges: [] };
  }

  // 表示対象のノードタイプのみをフィルタリング
  const filteredNodes = data.nodes.filter(
    (node) => nodeTypeFilters[node.nodeType as NodeType] !== false
  );

  // 有効なエッジのみを保持
  const validEdges = filterValidConnections(filteredNodes, data.edges);

  return {
    nodes: filteredNodes,
    edges: validEdges,
  };
};

// 組み合わせフィルタリング（すべての条件を統合）
export const filterNetworkData = (
  data: NetworkData,
  settings: NetworkSettingsProps
): NetworkData => {
  if (!data?.nodes || !data?.edges) {
    return { nodes: [], edges: [] };
  }

  // 0. edge.virtualを除外するためのフィルタリング
  const filteredEdges = data.edges.filter(
    (edge: Edge) => edge.virtual != true // virtualなエッジは除外
  );

  let filteredData = { nodes: data.nodes, edges: filteredEdges };

  // フィルタリングを順に適用
  const nodeIdsArray = Object.values(settings.filters || { nodeIds: [] });
  // 1. まずフィルタ情報でフィルタリング（タグデータがある場合）
  if (nodeIdsArray && nodeIdsArray.length > 0) {
    filteredData = filterByFilterItems(filteredData, settings.filters);
  }

  // 2. 重みと頻度でフィルタリング
  filteredData = filterByWeightAndFrequency(
    filteredData,
    settings.minEdgeWeight,
    settings.minNodeWeight,
    settings.minNodeFrequency
  );

  // 3. ドキュメントゼロのタグを除外
  filteredData = filterTagsWithoutDocuments(
    filteredData,
    settings.hideTagsWithoutDocuments
  );

  // 4. ソーシャルイシューのみ表示
  filteredData = filterBySocialIssues(
    filteredData,
    settings.showOnlySocialIssues
  );

  // 5. ノードタイプフィルター
  filteredData = filterByNodeType(filteredData, settings.nodeTypeFilters);

  return filteredData;
};

export interface NetworkSettingsProps {
  filters: NodeIDs | undefined; // 初期状態ではフィルターなし
  minEdgeWeight: number;
  minNodeWeight: number;
  minNodeFrequency: number;
  hideTagsWithoutDocuments: boolean;
  showOnlySocialIssues: boolean;
  nodeTypeFilters: NodeTypeFilterSettings;
}

// デフォルト値を持つオブジェクト
export const DEFAULT_NETWORK_SETTINGS: NetworkSettingsProps = {
  filters: undefined, // 初期状態ではフィルターなし
  minEdgeWeight: 1,
  minNodeWeight: 1,
  minNodeFrequency: 1,
  hideTagsWithoutDocuments: false,
  showOnlySocialIssues: false,
  nodeTypeFilters: DEFAULT_NODE_TYPE_FILTERS,
};

// メインのフィルタ関数をエクスポート
export const networkConditions = (
  networkData: NetworkData,
  settings: NetworkSettingsProps
): NetworkData => {
  return filterNetworkData(networkData, settings);
};
